import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate } from '@azure/msal-react';
import { useApolloClient } from '@apollo/client';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { useEffect, useRef } from 'react';

import en_GB from './_locale/en-GB.json';
import { GraphQLContainer } from './containers/graphql-container/graphql.container';
import { RouterContainer } from './containers/router-container/router-container';

import { ErrorBoundary } from 'react-error-boundary';
import { ErrorRender } from './app.error';
import { DialogContainer } from './containers/dialog-container/dialog-container.component';
import { ToastContainer } from './containers/toast-container/toast.container';
import { usePermissions } from '@/hooks/use-permissions/use-permissions.hook';
import { useBusinessUnitStore } from '@/store/business-unit.store';
import { useWorkAsStore } from '@/store/work-as.store';

type AppProps = {
  instance: PublicClientApplication;
};

const LOCALE = 'en-GB';

// This component's only job is to run logic that should execute once an authenticated user is present.
// It renders nothing itself.
const AppStateInitializer = () => {
  const apolloClient = useApolloClient();
  const { consumerBusinessUnits } = usePermissions();
  const { activeBusinessUnit, setActiveBusinessUnit } = useBusinessUnitStore();
  const { activeSubstitution } = useWorkAsStore();
  const previousRefreshContextRef = useRef({
    activeBusinessUnit,
    activeSubstitutionInitiatorId: activeSubstitution?.initiatorId ?? null,
  });

  useEffect(() => {
    if (!activeBusinessUnit && consumerBusinessUnits.length > 0) {
      // Set default active business unit to the first available consumer business unit
      setActiveBusinessUnit(consumerBusinessUnits[0]);
    }
  }, [consumerBusinessUnits, activeBusinessUnit, setActiveBusinessUnit]);

  useEffect(() => {
    // Refetch all active queries whenever the active business unit or substitution context changes, to ensure data is up to date with the current context
    // Known issue: Changing active business unit on certain pages might cause permission error
    const activeSubstitutionInitiatorId = activeSubstitution?.initiatorId ?? null;
    const previousRefreshContext = previousRefreshContextRef.current;
    const refreshContextChanged =
      previousRefreshContext.activeBusinessUnit !== activeBusinessUnit ||
      previousRefreshContext.activeSubstitutionInitiatorId !== activeSubstitutionInitiatorId;

    if (refreshContextChanged) {
      previousRefreshContextRef.current = {
        activeBusinessUnit,
        activeSubstitutionInitiatorId,
      };
      void apolloClient.refetchQueries({
        include: 'active',
      });
    }
  }, [activeBusinessUnit, activeSubstitution, apolloClient]);

  return null;
};

const App = ({ instance }: AppProps) => {
  return (
    <ErrorBoundary fallbackRender={ErrorRender}>
      <MsalProvider instance={instance}>
        <IntlProvider
          onError={(err) => {
            if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION) {
              // react-intl falls back to defaultMessage, so this must never throw but we log it for visibility
              // eslint-disable-next-line no-console
              console.info(
                `%cMISSING TRANSLATION: "${String(err.descriptor?.id ?? 'unknown')}" for locale "${LOCALE}", using default message (${String(err.descriptor?.defaultMessage ?? '')}) as fallback.`,
                'color: #eab308',
              );
              return;
            }
            throw err;
          }}
          locale={LOCALE}
          messages={{
            ...en_GB,
          }}
        >
          <GraphQLContainer>
            <RouterContainer />
            <AuthenticatedTemplate>
              <AppStateInitializer />
            </AuthenticatedTemplate>
            <DialogContainer />
            <ToastContainer />
          </GraphQLContainer>
        </IntlProvider>
      </MsalProvider>
    </ErrorBoundary>
  );
};

export default App;
